import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { FieldHeader, FieldHelpText, ClockIcon } from './FieldParts';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';
import FieldPopover from './datetime/FieldPopover';
import TimeWheelPanel from './datetime/TimeWheelPanel';
import { parse24, to24 } from './datetime/dateutils';

/** "09 : 30 AM" — the trigger format from the design. */
const formatTrigger = (hhmm: string): string => {
  const t = parse24(hhmm);
  return `${String(t.hour).padStart(2, '0')} : ${String(t.minute).padStart(2, '0')} ${t.ampm}`;
};

interface Props {
  element: DocumentElementType;
}

/**
 * Render an hh:mm (24h) string as a human-readable Australian-English
 * time (e.g. "2:30 pm"). Falls back to the raw string if it doesn't
 * parse, so a legacy value is never swallowed.
 */
const formatTime = (raw: string): string => {
  const m = /^(\d{2}):(\d{2})/.exec(raw);
  if (!m) return raw;
  const d = new Date();
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const TimePicker: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    defaultValue?: string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue);
  const label = data?.label || 'Time';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = data?.required || false;

  // Read-only (e.g. a completed result): render the chosen time as
  // static text — no time input, no picker.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {displayValue ? (
            formatTime(displayValue)
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <FieldPopover
        id={element.id}
        icon={<ClockIcon />}
        displayText={displayValue ? formatTrigger(displayValue) : ''}
        placeholder="-- : -- --"
      >
        {() => (
          <div style={{ maxWidth: 240, margin: '0 auto' }}>
            <TimeWheelPanel
              time={parse24(displayValue)}
              onChange={(t) => setValue(to24(t))}
            />
          </div>
        )}
      </FieldPopover>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default TimePicker;
